import Game from '@/models/Game';
import Team from '@/models/Team';
import TeamMatch from '@/models/TeamMatch';
import {
  GameStage,
  TeamMatchFormat,
  TeamMode,
  TeamPairingMode,
} from '@/services/utils/enums';
import { buildTeamBoardPairingAssignments } from '@/services/pairings/TeamMatchPairingService';
import { getWithdrawalTermination } from '@/services/utils/globalTools';
import { translate } from '@/i18n';

class GameFactory {
  /**
   * Создаёт Game[] швейцарского тура для индивидуального турнира.
   * Метод только создаёт данные в памяти и не сохраняет их через API.
   * @param {import('@/models/Tournament').default} tournament
   * @param {Array<Object>} pairs
   * @param {number} roundNumber
   * @returns {Game[]}
   */
  createIndividualSwissGames(tournament, pairs, roundNumber) {
    let nextNumber = tournament.getLastGameNumber();
    const newGames = [];
    const controlType = tournament.getControlType();

    pairs.forEach((pair) => {
      const whitePlayer = pair.side1Id
        ? this.#getPlayerEntityForParticipant(tournament, pair.side1Id)
        : null;
      const blackPlayer = pair.side2Id
        ? this.#getPlayerEntityForParticipant(tournament, pair.side2Id)
        : null;
      const game = this.#createBaseGame({
        tournament,
        whitePlayerIds: whitePlayer?.id ? [whitePlayer.id] : [],
        blackPlayerIds: blackPlayer?.id ? [blackPlayer.id] : [],
        whiteSide: this.#buildSide([this.#buildSideEntry(pair.side1Id ?? null, whitePlayer?.id ?? null)]),
        blackSide: this.#buildSide([this.#buildSideEntry(pair.side2Id ?? null, blackPlayer?.id ?? null)]),
        round: roundNumber,
        cycle: null,
        number: ++nextNumber,
        board: pair.board,
        stage: GameStage.REGULAR,
        isBye: pair.isBye,
        rated: tournament.rated,
        controlType,
      });

      this.#applySinglePlayerRatings(game, whitePlayer, blackPlayer, controlType);

      if (pair.isBye) {
        this.#applyByeResult(game, tournament.getSwissSettings()?.byePoints ?? 1);
      }

      newGames.push(game);
    });

    return newGames;
  }

  /**
   * Создаёт Game[] кругового цикла для индивидуального турнира.
   * Метод только создаёт данные в памяти и не сохраняет их через API.
   * @param {import('@/models/Tournament').default} tournament
   * @param {{cycleNumber: number, rounds: Array<Object>}} cycle
   * @returns {Game[]}
   */
  createIndividualRoundRobinCycleGames(tournament, cycle) {
    let nextNumber = tournament.getLastGameNumber();
    const newGames = [];
    const controlType = tournament.getControlType();

    cycle.rounds.forEach((round) => {
      round.pairs.forEach((pair) => {
        const whitePlayer = pair.side1Id
          ? this.#getPlayerEntityForParticipant(tournament, pair.side1Id)
          : null;
        const blackPlayer = pair.side2Id
          ? this.#getPlayerEntityForParticipant(tournament, pair.side2Id)
          : null;
        const game = this.#createBaseGame({
          tournament,
          whitePlayerIds: whitePlayer?.id ? [whitePlayer.id] : [],
          blackPlayerIds: blackPlayer?.id ? [blackPlayer.id] : [],
          whiteSide: this.#buildSide([this.#buildSideEntry(pair.side1Id ?? null, whitePlayer?.id ?? null)]),
          blackSide: this.#buildSide([this.#buildSideEntry(pair.side2Id ?? null, blackPlayer?.id ?? null)]),
          round: round.roundNumber,
          cycle: round.cycleNumber,
          number: ++nextNumber,
          board: pair.board,
          stage: GameStage.REGULAR,
          isBye: Boolean(pair.isBye),
          rated: tournament.rated,
          controlType,
        });

        this.#applySinglePlayerRatings(game, whitePlayer, blackPlayer, controlType);

        if (!game.isBye) {
          game.applyWithdrawalTermination(getWithdrawalTermination({
            whiteSideParticipantId: game.getSideParticipantId?.('white') ?? null,
            blackSideParticipantId: game.getSideParticipantId?.('black') ?? null,
            isParticipantActive: (participantId) => tournament.isParticipantActive(participantId),
          }));
        }

        newGames.push(game);
      });
    });

    return newGames;
  }

  /**
   * Создаёт одну индивидуальную Game из ручного запроса формы.
   * Метод только создаёт данные в памяти и не сохраняет их через API.
   * @param {import('@/models/Tournament').default} tournament
   * @param {Object} request
   * @returns {Game}
   */
  createManualIndividualGame(tournament, request = {}) {
    const stage = request.stage ?? GameStage.REGULAR;
    const controlType = request.controlType || (stage === GameStage.REGULAR
      ? tournament.getControlType?.()
      : tournament.getControlTypeForElimination?.());
    const whiteEntry = this.#normalizeManualSideEntry(tournament, request.whiteSide);
    const blackEntry = this.#normalizeManualSideEntry(tournament, request.blackSide);
    const whitePlayerId = whiteEntry?.player?.id ?? null;
    const blackPlayerId = blackEntry?.player?.id ?? null;
    const whitePlayer = whiteEntry?.player ?? null;
    const blackPlayer = blackEntry?.player ?? null;
    const game = this.#createBaseGame({
      tournament,
      whitePlayerIds: whitePlayerId ? [whitePlayerId] : [],
      blackPlayerIds: blackPlayerId ? [blackPlayerId] : [],
      whiteSide: this.#sideFromParticipantEntries([whiteEntry]),
      blackSide: this.#sideFromParticipantEntries([blackEntry]),
      round: request.round ?? null,
      cycle: request.cycle ?? null,
      number: request.number ?? null,
      board: request.board ?? null,
      stage,
      stageNumber: request.stageNumber ?? null,
      level: request.level ?? null,
      bracketIndex: request.bracketIndex ?? null,
      isBye: Boolean(request.isBye),
      rated: Boolean(request.rated),
      controlType,
    });

    Object.assign(game, {
      createdAt: request.createdAt ?? game.createdAt,
      updatedAt: request.updatedAt ?? game.updatedAt,
    });
    this.#applySinglePlayerRatings(game, whitePlayer, blackPlayer, controlType);
    return game;
  }

  /**
   * Создаёт Game[] плей-офф для индивидуальных пар.
   * Добавляет созданные Game в турнир, как и прежний flow плей-офф.
   * @param {import('@/models/Tournament').default} tournament
   * @param {string} stage
   * @param {Array<Object>} pairs
   * @param {number} [stageNumber=1]
   * @param {number} [level=1]
   * @param {Object} [options={}]
   * @param {number} [options.startBoard=1]
   * @returns {Game[]}
   */
  createIndividualEliminationGames(tournament, stage, pairs, stageNumber = 1, level = 1, {
    startBoard = 1,
  } = {}) {
    let board = startBoard;
    let number = tournament.getLastGameNumber();
    const newGames = [];

    for (const pair of pairs) {
      const { side1, side2, bracketIndex } = pair;
      const seriesRound = pair.seriesRound ?? 1;
      const side1ParticipantId = side1?.meta?.participantId ?? side1?.id ?? null;
      const side2ParticipantId = side2?.meta?.participantId ?? side2?.id ?? null;
      const side1PlayerId = this.#resolvePlayerIdForEliminationSide(tournament, side1);
      const side2PlayerId = this.#resolvePlayerIdForEliminationSide(tournament, side2);
      const side1Entry = {
        side: side1,
        participantId: side1ParticipantId,
        playerId: side1PlayerId,
      };
      const side2Entry = {
        side: side2,
        participantId: side2ParticipantId,
        playerId: side2PlayerId,
      };

      let whiteEntry = side1Entry;
      let blackEntry = side2Entry;

      if (!pair.isBye && seriesRound === 1) {
        const colorRule = tournament.getEliminationSettings()?.colorRule ?? 'random';
        if (colorRule === 'random') {
          if (Math.random() < 0.5) {
            [whiteEntry, blackEntry] = [side2Entry, side1Entry];
          }
        } else if (colorRule === 'leaders-white') {
          whiteEntry = side1Entry;
          blackEntry = side2Entry;
        } else if (colorRule === 'leaders-black') {
          whiteEntry = side2Entry;
          blackEntry = side1Entry;
        }
      } else if (!pair.isBye) {
        const previousGames = tournament.getEliminationGames().filter(
          (game) => {
            if (game.stage !== stage) return false;
            return (
              (game.sideHasParticipant?.('white', side1ParticipantId) && game.sideHasParticipant?.('black', side2ParticipantId)) ||
              (game.sideHasParticipant?.('white', side2ParticipantId) && game.sideHasParticipant?.('black', side1ParticipantId))
            );
          }
        );

        const lastGame = previousGames.sort((a, b) => (b.round || 0) - (a.round || 0))[0];
        if (lastGame) {
          const lastWhiteParticipantId = lastGame.getSideParticipantId?.('white') ?? null;
          const lastBlackParticipantId = lastGame.getSideParticipantId?.('black') ?? null;
          whiteEntry = lastBlackParticipantId === side1ParticipantId ? side1Entry : side2Entry;
          blackEntry = lastWhiteParticipantId === side1ParticipantId ? side1Entry : side2Entry;
        }
      }

      const whitePlayerId = whiteEntry?.playerId ?? null;
      const blackPlayerId = blackEntry?.playerId ?? null;
      const whiteSideParticipantId = whiteEntry?.participantId ?? null;
      const blackSideParticipantId = blackEntry?.participantId ?? null;
      const controlType = tournament.getControlTypeForElimination();
      const game = this.#createBaseGame({
        tournament,
        whitePlayerIds: whitePlayerId ? [whitePlayerId] : [],
        blackPlayerIds: blackPlayerId ? [blackPlayerId] : [],
        whiteSide: this.#buildSide([this.#buildSideEntry(whiteSideParticipantId, whitePlayerId)]),
        blackSide: this.#buildSide([this.#buildSideEntry(blackSideParticipantId, blackPlayerId)]),
        number: ++number,
        board: pair.isBye ? null : board++,
        stage,
        round: seriesRound,
        stageNumber,
        level,
        bracketIndex,
        isBye: Boolean(pair.isBye),
        rated: pair.isBye ? false : tournament.rated,
        controlType,
      });

      const whiteObj = tournament.players.find((player) => player.id === whitePlayerId);
      const blackObj = tournament.players.find((player) => player.id === blackPlayerId);
      this.#applySinglePlayerRatings(game, whiteObj, blackObj, controlType);

      if (!game.isBye) {
        game.applyWithdrawalTermination(getWithdrawalTermination({
          whiteSideParticipantId: game.getSideParticipantId?.('white') ?? null,
          blackSideParticipantId: game.getSideParticipantId?.('black') ?? null,
          isParticipantActive: (participantId) => tournament.isParticipantActive(participantId),
        }));
      }

      tournament.addGame(game);
      newGames.push(game);
    }

    return newGames;
  }

  #resolvePlayerIdForEliminationSide(tournament, side) {
    if (!side) return null;
    const participantId = side?.meta?.participantId ?? side?.id ?? null;
    const currentPlayer = participantId
      ? this.#getPlayerEntityForParticipant(tournament, participantId)
      : null;
    return currentPlayer?.id ?? side?.entity?.id ?? side?.id ?? null;
  }

  #getPlayerEntityForParticipant(tournament, participantId) {
    const participant = tournament.getParticipant?.(participantId) ?? null;
    if (participant?.type !== 'player') return null;
    return participant.entity ?? null;
  }

  #buildSideEntry(participantId, playerId) {
    if (!playerId) return null;
    return {
      participantId: participantId ?? null,
      playerId,
    };
  }

  #buildSide(entries = []) {
    return {
      entries: (Array.isArray(entries) ? entries : [])
        .filter((entry) => entry?.playerId)
        .map((entry) => ({
          participantId: entry.participantId ?? null,
          playerId: entry.playerId,
        })),
    };
  }

  #buildSideFromPlayers(players = [], tournament = null) {
    return this.#buildSide(
      (Array.isArray(players) ? players : []).map((player) => {
        const participant = player?.id
          ? tournament?.getActiveParticipantByCurrentEntityId?.(player.id) ?? null
          : null;
        return this.#buildSideEntry(participant?.id ?? null, player?.id ?? null);
      })
    );
  }

  #resolveTeamParticipantEntries(tournament, team) {
    if (
      team?.id
      && tournament?.teamSettings?.teamMode === TeamMode.FIXED
      && typeof tournament.getParticipants === 'function'
      && typeof tournament.getFixedTeamForParticipant === 'function'
    ) {
      return tournament.getParticipants({ type: 'player' })
        .filter((participant) => tournament.isParticipantActive?.(participant.id))
        .filter((participant) => tournament.getFixedTeamForParticipant(participant)?.id === team.id)
        .map((participant) => ({
          participantId: participant.id,
          player: participant.entity,
        }))
        .filter((entry) => entry.player?.id);
    }

    const seenParticipantIds = new Set();
    return (Array.isArray(team?.players) ? team.players : [])
      .map((player) => {
        if (!player?.id) return null;
        const participant = tournament.getActiveParticipantByCurrentEntityId?.(player.id) ?? null;
        if (!participant?.id || !tournament.isParticipantActive?.(participant.id) || !participant.entity?.id) {
          return null;
        }
        if (seenParticipantIds.has(participant.id)) return null;
        seenParticipantIds.add(participant.id);
        return {
          participantId: participant.id,
          player: participant.entity,
        };
      })
      .filter(Boolean);
  }

  #entriesToPlayers(entries = []) {
    return (Array.isArray(entries) ? entries : [])
      .map((entry) => entry?.player)
      .filter(Boolean);
  }

  #entryForPlayer(entries = [], player = null) {
    if (!player?.id) return null;
    return (Array.isArray(entries) ? entries : [])
      .find((entry) => entry?.player?.id === player.id) ?? null;
  }

  #sideFromParticipantEntries(entries = []) {
    return this.#buildSide(
      (Array.isArray(entries) ? entries : []).map((entry) => (
        this.#buildSideEntry(entry?.participantId ?? null, entry?.player?.id ?? null)
      ))
    );
  }

  #teamWithPlayers(team, players = []) {
    if (!team) return null;
    const normalizedPlayers = Array.isArray(players) ? players.filter(Boolean) : [];
    const name = normalizedPlayers.map((player) => player?.name).filter(Boolean).join('/') || team.name;
    return {
      ...team,
      name,
      players: normalizedPlayers,
    };
  }

  /**
   * Создаёт Game[] для одного матча временных random-команд.
   * Метод только создаёт данные в памяти и не сохраняет их через API.
   * @param {import('@/models/Tournament').default} tournament
   * @param {Object} pair
   * @param {Object} [options={}]
   * @returns {{games: Game[], nextNumberValue: number, nextBoardValue: number}}
   */
  createRandomTeamMatch(tournament, pair, options = {}) {
    const ctx = this.#buildRandomContext(tournament, pair, options);
    return this.#createGamesInTeamMatch(ctx);
  }

  /**
   * Создаёт Game[] для одного матча fixed-команд.
   * Метод только создаёт данные в памяти и не сохраняет их через API.
   * @param {import('@/models/Tournament').default} tournament
   * @param {Object} pair
   * @param {Object} [options={}]
   * @returns {{games: Game[], nextNumberValue: number, nextBoardValue: number}}
   */
  createFixedTeamMatch(tournament, pair, options = {}) {
    const ctx = this.#buildFixedContext(tournament, pair, options);
    return this.#createGamesInTeamMatch(ctx);
  }

  /**
   * Создаёт Game[] для вручную заданного командного матча.
   * UI передаёт только выбранные стороны, игроков, цвета и доски; ключ матча,
   * teamContext, стороны Game и рейтинги собираются здесь по тем же правилам,
   * что и при автоматическом создании.
   * @param {import('@/models/Tournament').default} tournament
   * @param {Object} draft
   * @returns {{games: Game[], nextNumberValue: number, nextBoardValue: number}}
   */
  createManualTeamMatch(tournament, draft = {}) {
    const stage = draft.stage ?? GameStage.REGULAR;
    const options = {
      round: draft.round,
      cycle: draft.cycle ?? null,
      startNumber: (Number(draft.number) || tournament.getLastGameNumber?.() || 0) - 1,
      startBoard: Number(draft.board) || 1,
      stage,
      stageNumber: draft.stageNumber ?? null,
      level: draft.level ?? null,
      bracketIndex: draft.bracketIndex ?? null,
    };
    const ctx = tournament.teamSettings?.teamMode === TeamMode.RANDOM
      ? this.#buildRandomContext(tournament, this.#buildManualRandomPair(tournament, draft), options)
      : this.#buildFixedContext(tournament, this.#buildManualFixedPair(draft), options);

    return this.#createManualDraftTeamMatchGames(ctx, draft);
  }

  /**
   * Собирает контекст создания матча временных random-команд.
   * Контекст унифицирует входные данные для всех вариантов team-match.
   * @param {import('@/models/Tournament').default} tournament
   * @param {Object} pair
   * @param {Object} [options={}]
   * @returns {Object}
   */
  #buildRandomContext(tournament, pair, {
    round,
    cycle = null,
    startNumber = 0,
    startBoard = 1,
    playerStandings = [],
    playerHistory = new Map(),
    mirrorRepeatPairColors = false,
    stage = GameStage.REGULAR,
    stageNumber = null,
    level = null,
    bracketIndex = null,
  } = {}) {
    if (tournament.teamSettings?.teamMode !== TeamMode.RANDOM) {
      throw new Error('Random team match generation is implemented only for random teams');
    }

    const side1Team = pair?.side1 ?? null;
    const side2Team = pair?.side2 ?? null;
    if (!side1Team?.players?.length) {
      throw new Error('Side1 random team has no players for team match generation');
    }

    const controlType = stage === GameStage.REGULAR
      ? tournament.getControlType()
      : tournament.getControlTypeForElimination();
    const teamMatchFormat = tournament.teamSettings?.teamMatchFormat ?? TeamMatchFormat.BOARD_SUM;
    const teamPairingMode = tournament.teamSettings?.teamPairingMode ?? TeamPairingMode.AUTO;
    const teamMatchKey = stage === GameStage.REGULAR
      ? TeamMatch.buildRandomKey(round, side1Team?.key, side2Team?.key)
      : TeamMatch.buildEliminationKey(stage, stageNumber, level, side1Team?.key, side2Team?.key, bracketIndex);

    return {
      tournament,
      pair,
      side1Team,
      side2Team,
      round,
      cycle,
      startNumber,
      startBoard,
      playerStandings,
      playerHistory,
      mirrorRepeatPairColors,
      stage,
      stageNumber,
      level,
      bracketIndex,
      controlType,
      teamMatchFormat,
      teamPairingMode,
      teamMatchKey,
      byePoints: tournament.getSwissSettings()?.byePoints ?? 1,
      isPlayerByeGroup: Boolean(pair?.isPlayerBye),
      isTeamBye: Boolean(pair?.isBye || !side2Team),
      byePlayers: Array.isArray(pair?.byePlayers) && pair.byePlayers.length
        ? this.#entriesToPlayers(this.#resolveTeamParticipantEntries(tournament, { players: pair.byePlayers }))
        : this.#entriesToPlayers(this.#resolveTeamParticipantEntries(tournament, side1Team)),
      avoidRepeatByes: true,
      sortBoardByesLast: true,
      getTeamEntries: (team) => this.#resolveTeamParticipantEntries(tournament, team),
      getTeamPlayers: (team) => this.#entriesToPlayers(this.#resolveTeamParticipantEntries(tournament, team)),
      getManualTeamPlayers: (team) => this.#entriesToPlayers(this.#resolveTeamParticipantEntries(tournament, team)),
      getManualIsBye: () => Boolean(pair?.isBye || !side2Team),
      getManualBoardsCount: (side1Players, side2Players) =>
        Math.max(side1Players.length, side2Players.length, 1),
      buildTeamContext: (whiteTeam, blackTeam, extra = {}) =>
        Game.buildRandomTeamContext({
          whiteTeam,
          blackTeam,
          teamMatchKey,
          ...extra,
        }),
    };
  }

  /**
   * Собирает контекст создания матча fixed-команд.
   * Находит команды турнира по id и готовит общие параметры для создания внутренних Game.
   * @param {import('@/models/Tournament').default} tournament
   * @param {Object} pair
   * @param {Object} [options={}]
   * @returns {Object}
   */
  #buildFixedContext(tournament, pair, {
    round,
    cycle = null,
    startNumber = 0,
    startBoard = 1,
    playerStandings = [],
    playerHistory = new Map(),
    mirrorRepeatPairColors = false,
    stage = GameStage.REGULAR,
    stageNumber = null,
    level = null,
    bracketIndex = null,
  } = {}) {
    if (tournament.teamSettings?.teamMode !== TeamMode.FIXED) {
      throw new Error('Team match generation is implemented only for fixed teams');
    }

    const side1Team = (tournament.teams || []).find((team) => team?.id === pair?.side1Id) ?? null;
    const side2Team = pair?.side2Id
      ? ((tournament.teams || []).find((team) => team?.id === pair.side2Id) ?? null)
      : null;

    if (!side1Team) {
      throw new Error('Side1 team not found for team match generation');
    }

    const controlType = stage === GameStage.REGULAR
      ? tournament.getControlType()
      : tournament.getControlTypeForElimination();
    const teamMatchFormat = tournament.teamSettings?.teamMatchFormat ?? TeamMatchFormat.BOARD_SUM;
    const teamPairingMode = tournament.teamSettings?.teamPairingMode ?? TeamPairingMode.AUTO;
    const teamMatchKey = stage === GameStage.REGULAR
      ? TeamMatch.buildRegularKey(round, cycle, pair?.side1Id, pair?.side2Id)
      : TeamMatch.buildEliminationKey(stage, stageNumber, level, pair?.side1Id, pair?.side2Id, bracketIndex);

    return {
      tournament,
      pair,
      side1Team,
      side2Team,
      round,
      cycle,
      startNumber,
      startBoard,
      playerStandings,
      playerHistory,
      mirrorRepeatPairColors,
      stage,
      stageNumber,
      level,
      bracketIndex,
      controlType,
      teamMatchFormat,
      teamPairingMode,
      teamMatchKey,
      byePoints: tournament.getSwissSettings()?.byePoints ?? 1,
      isPlayerByeGroup: false,
      isTeamBye: Boolean(pair?.isBye || !side2Team),
      avoidRepeatByes: false,
      sortBoardByesLast: false,
      getTeamPlayers: (team) => tournament.getActiveTeamPlayers(team),
      getTeamEntries: (team) => this.#resolveTeamParticipantEntries(tournament, team),
      getManualTeamPlayers: (team) => {
        const teamSize = tournament.getTeamSize();
        return this.#entriesToPlayers(this.#resolveTeamParticipantEntries(tournament, team)).slice(0, teamSize || undefined);
      },
      getManualIsBye: (side2Players) => Boolean(pair?.isBye || !side2Team || side2Players.length === 0),
      getManualBoardsCount: (side1Players, side2Players) => {
        const teamSize = tournament.getTeamSize();
        return teamSize || Math.max(side1Players.length, side2Players.length, 1);
      },
      buildTeamContext: (whiteTeam, blackTeam) =>
        this.#buildFixedTeamContext(whiteTeam, blackTeam, teamMatchKey),
    };
  }

  #buildManualFixedPair(draft = {}) {
    const leftTeamId = draft.leftTeamId || null;
    const rightTeamId = draft.rightTeamId || null;
    const side1Id = leftTeamId || rightTeamId || null;
    const side2Id = leftTeamId ? rightTeamId : null;

    return {
      side1Id,
      side2Id,
      isBye: !leftTeamId || !rightTeamId,
    };
  }

  #buildManualRandomPair(tournament, draft = {}) {
    const leftEntries = this.#manualSideEntries(tournament, draft, 'left');
    const rightEntries = this.#manualSideEntries(tournament, draft, 'right');
    const leftPlayerIds = leftEntries.map((entry) => entry.player?.id).filter(Boolean);
    const rightPlayerIds = rightEntries.map((entry) => entry.player?.id).filter(Boolean);
    const buildTeam = (side, entries) => ({
      key: this.#buildManualRandomTeamKey(draft, side, entries.map((entry) => entry.participantId).filter(Boolean)),
      name: translate('serviceMessages.teamNumber', { number: side === 'left' ? 1 : 2 }),
      playerIds: entries.map((entry) => entry.player?.id).filter(Boolean),
      players: entries.map((entry) => entry.player).filter(Boolean),
      temporary: true,
    });

    return {
      side1: buildTeam('left', leftEntries),
      side2: rightEntries.length ? buildTeam('right', rightEntries) : null,
      isBye: rightPlayerIds.length === 0,
    };
  }

  #buildManualRandomTeamKey(draft = {}, side, playerIds = []) {
    const sidePart = side === 'left' ? 'S1' : 'S2';
    const stagePart = draft.stage === GameStage.REGULAR
      ? `R${draft.round ?? 0}`
      : `${draft.stage ?? GameStage.REGULAR}-S${draft.stageNumber ?? 1}-L${draft.level ?? 1}-B${draft.bracketIndex ?? 0}`;
    const playersPart = playerIds.length ? playerIds.join('_') : 'bye';
    return `${stagePart}-${sidePart}-${playersPart}`;
  }

  #normalizeManualSideEntry(tournament, sideEntry = null) {
    if (!sideEntry?.participantId || !sideEntry?.playerId) return null;
    const player = tournament.players?.find((item) => item?.id === sideEntry.playerId) ?? null;
    if (!player?.id) return null;
    return {
      participantId: sideEntry.participantId,
      player,
    };
  }

  #manualSideEntries(tournament, draft = {}, side) {
    const key = side === 'left' ? 'leftSide' : 'rightSide';
    const rows = Array.isArray(draft.rows) ? draft.rows : [];
    const seen = new Set();
    return rows
      .map((row) => this.#normalizeManualSideEntry(tournament, row?.[key]))
      .filter((entry) => {
        if (!entry?.participantId || !entry?.player?.id) return false;
        const entryKey = `${entry.participantId}:${entry.player.id}`;
        if (seen.has(entryKey)) return false;
        seen.add(entryKey);
        return true;
      })
      .filter(Boolean);
  }

  #manualPlayerIdsFromEntries(entries = []) {
    return (Array.isArray(entries) ? entries : [])
      .map((entry) => entry?.player?.id ?? null)
      .filter(Boolean);
  }

  #manualTeamForSide(ctx, draft = {}, side, entries = []) {
    if (ctx.tournament.teamSettings?.teamMode === TeamMode.RANDOM) {
      return side === 'left' ? ctx.side1Team : ctx.side2Team;
    }

    const teamId = side === 'left' ? draft.leftTeamId : draft.rightTeamId;
    const team = teamId
      ? (ctx.tournament.teams || []).find((item) => item?.id === teamId) ?? null
      : null;

    return team ? this.#teamWithPlayers(team, this.#playersByIds(ctx.tournament, this.#manualPlayerIdsFromEntries(entries))) : null;
  }

  #manualEntriesForSide(ctx, team, entries = []) {
    if (!team || !entries.length) return [];
    return entries.filter((entry) => entry?.participantId && entry?.player?.id);
  }

  #playersByIds(tournament, playerIds = []) {
    return (Array.isArray(playerIds) ? playerIds : [])
      .map((playerId) => tournament.players?.find((player) => player?.id === playerId))
      .filter(Boolean);
  }

  #createManualDraftTeamMatchGames(ctx, draft = {}) {
    const rows = Array.isArray(draft.rows) ? draft.rows : [];
    const games = [];
    const isCommonBoard = ctx.teamMatchFormat === TeamMatchFormat.COMMON_BOARD;
    const baseNumber = Number(draft.number) || (ctx.startNumber + 1);
    const baseBoard = Number(draft.board) || ctx.startBoard || 1;

    if (isCommonBoard) {
      const leftColor = draft.leftColor === 'black' ? 'black' : 'white';
      const leftSideEntries = this.#manualSideEntries(ctx.tournament, draft, 'left');
      const rightSideEntries = this.#manualSideEntries(ctx.tournament, draft, 'right');
      const leftTeam = this.#manualTeamForSide(ctx, draft, 'left', leftSideEntries);
      const rightTeam = this.#manualTeamForSide(ctx, draft, 'right', rightSideEntries);
      const whiteSideEntries = leftColor === 'white' ? leftSideEntries : rightSideEntries;
      const blackSideEntries = leftColor === 'white' ? rightSideEntries : leftSideEntries;
      const whiteTeam = leftColor === 'white' ? leftTeam : rightTeam;
      const blackTeam = leftColor === 'white' ? rightTeam : leftTeam;
      const whiteEntries = this.#manualEntriesForSide(ctx, whiteTeam, whiteSideEntries);
      const blackEntries = this.#manualEntriesForSide(ctx, blackTeam, blackSideEntries);
      const whitePlayerIds = whiteEntries.map((entry) => entry.player?.id).filter(Boolean);
      const blackPlayerIds = blackEntries.map((entry) => entry.player?.id).filter(Boolean);
      const game = this.#createTeamBaseGame(ctx, {
        whitePlayerIds,
        blackPlayerIds,
        whiteSide: this.#sideFromParticipantEntries(whiteEntries),
        blackSide: this.#sideFromParticipantEntries(blackEntries),
        number: baseNumber,
        board: baseBoard,
        isBye: whitePlayerIds.length === 0 || blackPlayerIds.length === 0,
        rated: ctx.tournament.rated,
        teamContext: ctx.buildTeamContext(whiteTeam, blackTeam),
      });

      this.#applyTeamRatings(game, whiteTeam, blackTeam, ctx.controlType, this.#playersByIds(ctx.tournament, whitePlayerIds), this.#playersByIds(ctx.tournament, blackPlayerIds));
      games.push(game);
      return this.#buildGeneratedGamesResult(games, baseNumber, baseBoard + 1);
    }

    rows.forEach((row, index) => {
      const leftColor = row?.leftColor === 'black' ? 'black' : 'white';
      const leftSideEntries = [this.#normalizeManualSideEntry(ctx.tournament, row?.leftSide)].filter(Boolean);
      const rightSideEntries = [this.#normalizeManualSideEntry(ctx.tournament, row?.rightSide)].filter(Boolean);
      const leftTeam = this.#manualTeamForSide(ctx, draft, 'left', leftSideEntries);
      const rightTeam = this.#manualTeamForSide(ctx, draft, 'right', rightSideEntries);
      const whiteSideEntries = leftColor === 'white' ? leftSideEntries : rightSideEntries;
      const blackSideEntries = leftColor === 'white' ? rightSideEntries : leftSideEntries;
      const whiteTeam = leftColor === 'white' ? leftTeam : rightTeam;
      const blackTeam = leftColor === 'white' ? rightTeam : leftTeam;
      const whiteEntries = this.#manualEntriesForSide(ctx, whiteTeam, whiteSideEntries);
      const blackEntries = this.#manualEntriesForSide(ctx, blackTeam, blackSideEntries);
      const whitePlayerIds = whiteEntries.map((entry) => entry.player?.id).filter(Boolean);
      const blackPlayerIds = blackEntries.map((entry) => entry.player?.id).filter(Boolean);
      const whitePlayer = whitePlayerIds[0]
        ? ctx.tournament.players?.find((player) => player?.id === whitePlayerIds[0])
        : null;
      const blackPlayer = blackPlayerIds[0]
        ? ctx.tournament.players?.find((player) => player?.id === blackPlayerIds[0])
        : null;
      const game = this.#createTeamBaseGame(ctx, {
        whitePlayerIds,
        blackPlayerIds,
        whiteSide: this.#sideFromParticipantEntries(whiteEntries),
        blackSide: this.#sideFromParticipantEntries(blackEntries),
        number: Number(row?.number) || baseNumber + index,
        board: Number(row?.board) || baseBoard + index,
        isBye: whitePlayerIds.length === 0 || blackPlayerIds.length === 0,
        rated: ctx.tournament.rated,
        teamContext: ctx.buildTeamContext(whiteTeam, blackTeam),
      });

      this.#applySinglePlayerRatings(game, whitePlayer, blackPlayer, ctx.controlType);
      games.push(game);
    });

    const nextNumber = games.length ? Math.max(...games.map((game) => Number(game?.number) || baseNumber)) : baseNumber;
    const nextBoard = games.length ? Math.max(...games.map((game) => Number(game?.board) || baseBoard)) + 1 : baseBoard;
    return this.#buildGeneratedGamesResult(games, nextNumber, nextBoard);
  }

  /**
   * Маршрутизирует создание внутренних Game одного командного матча по режиму отображения/подсчёта.
   * @param {Object} ctx
   * @returns {{games: Game[], nextNumberValue: number, nextBoardValue: number}}
   */
  #createGamesInTeamMatch(ctx) {
    if (ctx.isPlayerByeGroup) {
      return this.#createPlayerByeGames(ctx);
    }

    if (ctx.teamPairingMode === TeamPairingMode.MANUAL) {
      return this.#createManualTeamMatchGames(ctx);
    }

    if (ctx.isTeamBye) {
      return this.#createTeamByeGames(ctx);
    }

    if (ctx.stage !== GameStage.REGULAR && Number(ctx.round) > 1) {
      const mirrored = this.#createMirroredTeamMatchGames(ctx);
      if (mirrored) return mirrored;
    }

    if (ctx.teamMatchFormat === TeamMatchFormat.COMMON_BOARD) {
      return this.#createCommonBoardTeamMatchGames(ctx);
    }

    return this.#createBoardTeamMatchGames(ctx);
  }

  /**
   * Создаёт группу индивидуальных bye-игр для лишних игроков random-team тура.
   * Это не командный bye: каждый игрок получает отдельную bye-партию внутри одной UI-группы.
   * @param {Object} ctx
   * @returns {{games: Game[], nextNumberValue: number, nextBoardValue: number}}
   */
  #createPlayerByeGames(ctx) {
    const { tournament, side1Team, controlType, byePoints, byePlayers } = ctx;
    const games = [];
    let nextNumber = ctx.startNumber;
    let nextBoard = ctx.startBoard;
    const byeEntries = this.#resolveTeamParticipantEntries(tournament, { players: byePlayers });

    if (!byePlayers.length) {
      throw new Error('Random team player bye has no players');
    }

    for (const player of byePlayers) {
      if (!player?.id) continue;

      const byeGame = this.#createTeamBaseGame(ctx, {
        whitePlayerIds: [player.id],
        blackPlayerIds: [],
        whiteSide: this.#sideFromParticipantEntries([this.#entryForPlayer(byeEntries, player)]),
        blackSide: this.#buildSide([]),
        number: ++nextNumber,
        board: nextBoard++,
        isBye: true,
        rated: tournament.rated,
        teamContext: ctx.buildTeamContext(this.#teamWithPlayers(side1Team, [player]), null, { isByeGroup: true }),
      });

      this.#applyByeResult(byeGame, byePoints);
      this.#applySinglePlayerRatings(byeGame, player, null, controlType);
      games.push(byeGame);
    }

    return this.#buildGeneratedGamesResult(games, nextNumber, nextBoard);
  }

  /**
   * Создаёт одну агрегирующую Game для ручного командного матча.
   * Конкретные досочные пары не создаются, результат выставляется на уровне команды.
   * @param {Object} ctx
   * @returns {{games: Game[], nextNumberValue: number, nextBoardValue: number}}
   */
  #createManualTeamMatchGames(ctx) {
    const { side1Team, side2Team, controlType, byePoints } = ctx;
    const games = [];
    let nextNumber = ctx.startNumber;
    let nextBoard = ctx.startBoard;
    const teamSize = ctx.tournament.getTeamSize?.() || null;
    const side1Entries = (ctx.getTeamEntries?.(side1Team) ?? []).slice(0, teamSize || undefined);
    const side2Entries = side2Team ? (ctx.getTeamEntries?.(side2Team) ?? []).slice(0, teamSize || undefined) : [];
    const side1Players = this.#entriesToPlayers(side1Entries);
    const side2Players = this.#entriesToPlayers(side2Entries);
    const game = this.#createTeamBaseGame(ctx, {
      whitePlayerIds: side1Players.map((player) => player?.id).filter(Boolean),
      blackPlayerIds: side2Players.map((player) => player?.id).filter(Boolean),
      whiteSide: this.#sideFromParticipantEntries(side1Entries),
      blackSide: this.#sideFromParticipantEntries(side2Entries),
      number: ++nextNumber,
      board: nextBoard,
      isBye: ctx.getManualIsBye(side2Players),
      rated: false,
      teamContext: ctx.buildTeamContext(
        this.#teamWithPlayers(side1Team, side1Players),
        this.#teamWithPlayers(side2Team, side2Players)
      ),
    });

    this.#applyTeamRatings(game, side1Team, side2Team, controlType, side1Players, side2Players);

    if (game.isBye) {
      this.#applyByeResult(game, byePoints);
    }

    games.push(game);
    nextBoard += ctx.getManualBoardsCount(side1Players, side2Players);
    return this.#buildGeneratedGamesResult(games, nextNumber, nextBoard);
  }

  /**
   * Создаёт bye-игры для всей команды.
   * Для каждого активного игрока команды создаётся отдельная Game, объединённая одним teamMatchKey.
   * @param {Object} ctx
   * @returns {{games: Game[], nextNumberValue: number, nextBoardValue: number}}
   */
  #createTeamByeGames(ctx) {
    const { tournament, side1Team, controlType, byePoints } = ctx;
    const games = [];
    let nextNumber = ctx.startNumber;
    let nextBoard = ctx.startBoard;
    const side1Entries = ctx.getTeamEntries?.(side1Team) ?? [];
    const side1Players = this.#entriesToPlayers(side1Entries);

    if (!side1Players.length) {
      throw new Error('Side1 team has no players for team bye generation');
    }

    for (const player of side1Players) {
      if (!player?.id) continue;

      const byeGame = this.#createTeamBaseGame(ctx, {
        whitePlayerIds: [player.id],
        blackPlayerIds: [],
        whiteSide: this.#sideFromParticipantEntries([this.#entryForPlayer(side1Entries, player)]),
        blackSide: this.#buildSide([]),
        number: ++nextNumber,
        board: nextBoard++,
        isBye: true,
        rated: tournament.rated,
        teamContext: ctx.buildTeamContext(this.#teamWithPlayers(side1Team, [player]), null),
      });

      this.#applyByeResult(byeGame, byePoints);
      this.#applySinglePlayerRatings(byeGame, player, null, controlType);
      games.push(byeGame);
    }

    return this.#buildGeneratedGamesResult(games, nextNumber, nextBoard);
  }

  /**
   * Создаёт следующую командную партию серии плей-офф зеркально предыдущей.
   * Состав команд и досочные пары сохраняются, цвета игроков и команд меняются местами.
   * @param {Object} ctx
   * @returns {{games: Game[], nextNumberValue: number, nextBoardValue: number}|null}
   */
  #createMirroredTeamMatchGames(ctx) {
    const previousTeamMatch = this.#findPreviousTeamMatchInSeries(ctx);
    if (!previousTeamMatch) return null;

    const previousGames = [...previousTeamMatch.games].sort((a, b) => {
      const aBoard = a?.board ?? Number.MAX_SAFE_INTEGER;
      const bBoard = b?.board ?? Number.MAX_SAFE_INTEGER;
      if (aBoard !== bBoard) return aBoard - bBoard;
      return (a?.number ?? 0) - (b?.number ?? 0);
    });
    const games = [];
    let nextNumber = ctx.startNumber;
    let nextBoard = ctx.startBoard;

    for (const previousGame of previousGames) {
      const whiteTeam = this.#resolveMirroredTeam(ctx, previousGame, 'black');
      const blackTeam = this.#resolveMirroredTeam(ctx, previousGame, 'white');
      const whiteSide = this.#resolveCurrentSideFromPreviousSide(ctx, previousGame, 'black', whiteTeam);
      const blackSide = this.#resolveCurrentSideFromPreviousSide(ctx, previousGame, 'white', blackTeam);
      const whitePlayerIds = Game.playerIdsFromSide(whiteSide);
      const blackPlayerIds = Game.playerIdsFromSide(blackSide);
      const game = this.#createTeamBaseGame(ctx, {
        whitePlayerIds,
        blackPlayerIds,
        whiteSide,
        blackSide,
        number: ++nextNumber,
        board: nextBoard++,
        isBye: Boolean(previousGame.isBye),
        rated: previousGame.rated,
        teamContext: ctx.buildTeamContext(
          this.#teamWithPlayers(whiteTeam, whitePlayerIds.map((playerId) => ctx.tournament.players.find((player) => player?.id === playerId))),
          this.#teamWithPlayers(blackTeam, blackPlayerIds.map((playerId) => ctx.tournament.players.find((player) => player?.id === playerId)))
        ),
      });
      const whitePlayer = whitePlayerIds[0]
        ? ctx.tournament.players.find((player) => player.id === whitePlayerIds[0])
        : null;
      const blackPlayer = blackPlayerIds[0]
        ? ctx.tournament.players.find((player) => player.id === blackPlayerIds[0])
        : null;

      if (ctx.teamMatchFormat === TeamMatchFormat.COMMON_BOARD) {
        const whitePlayers = whitePlayerIds
          .map((playerId) => ctx.tournament.players.find((player) => player.id === playerId))
          .filter(Boolean);
        const blackPlayers = blackPlayerIds
          .map((playerId) => ctx.tournament.players.find((player) => player.id === playerId))
          .filter(Boolean);
        this.#applyTeamRatings(game, whiteTeam, blackTeam, ctx.controlType, whitePlayers, blackPlayers);
      } else {
        this.#applySinglePlayerRatings(game, whitePlayer, blackPlayer, ctx.controlType);
      }

      if (game.isBye && ctx.teamMatchFormat !== TeamMatchFormat.FIRST_WIN) {
        game.setResult(whitePlayerIds.length ? '1-0' : '0-1');
      }

      games.push(game);
    }

    return this.#buildGeneratedGamesResult(games, nextNumber, nextBoard);
  }

  /**
   * Находит предыдущий командный матч той же серии плей-офф.
   * @param {Object} ctx
   * @returns {import('@/models/TeamMatch').default|null}
   */
  #findPreviousTeamMatchInSeries(ctx) {
    const side1Id = this.#getTeamIdentity(ctx.side1Team);
    const side2Id = this.#getTeamIdentity(ctx.side2Team);
    if (!side1Id || !side2Id) return null;

    return ctx.tournament.getTeamMatches(ctx.tournament.getEliminationGames())
      .filter((teamMatch) =>
        teamMatch.stage === ctx.stage &&
        Number(teamMatch.round ?? 0) < Number(ctx.round ?? 1) &&
        ((teamMatch.side1?.id === side1Id && teamMatch.side2?.id === side2Id) ||
         (teamMatch.side1?.id === side2Id && teamMatch.side2?.id === side1Id))
      )
      .sort((a, b) => (b.round ?? 0) - (a.round ?? 0))[0] ?? null;
  }

  /**
   * Возвращает команду из текущей пары по цвету команды в предыдущей Game.
   * @param {Object} ctx
   * @param {Game} previousGame
   * @param {'white'|'black'} color
   * @returns {Object|null}
   */
  #resolveMirroredTeam(ctx, previousGame, color) {
    const previousTeamId = color === 'white'
      ? (previousGame.getWhiteTeamId?.() ?? previousGame.getWhiteTeamKey?.())
      : (previousGame.getBlackTeamId?.() ?? previousGame.getBlackTeamKey?.());
    const side1Id = this.#getTeamIdentity(ctx.side1Team);
    const side2Id = this.#getTeamIdentity(ctx.side2Team);

    if (previousTeamId === side1Id) return ctx.side1Team;
    if (previousTeamId === side2Id) return ctx.side2Team;
    return null;
  }

  /**
   * Возвращает устойчивый идентификатор команды: id для fixed, key для random.
   * @param {Object|null} team
   * @returns {string|null}
   */
  #getTeamIdentity(team) {
    return team?.key ?? team?.id ?? null;
  }

  #resolveCurrentSideFromPreviousSide(ctx, previousGame, color, team) {
    const previousEntries = previousGame.getSideEntries?.(color) ?? [];
    if (previousEntries.length) {
      return this.#buildSide(previousEntries.map((entry) => {
        const participant = entry?.participantId
          ? ctx.tournament.getParticipant?.(entry.participantId)
          : null;
        const playerId = participant?.entity?.id ?? entry?.playerId ?? null;
        return this.#buildSideEntry(entry?.participantId ?? null, playerId);
      }));
    }

    const previousPlayerIds = color === 'white'
      ? previousGame.whitePlayerIds
      : previousGame.blackPlayerIds;
    const fallbackEntries = this.#resolveTeamParticipantEntries(ctx.tournament, team)
      .filter((entry) => previousPlayerIds?.includes(entry?.player?.id));
    return this.#sideFromParticipantEntries(fallbackEntries);
  }

  /**
   * Создаёт одну общую Game для common-board командного матча.
   * В одной партии участвуют все игроки стороны.
   * @param {Object} ctx
   * @returns {{games: Game[], nextNumberValue: number, nextBoardValue: number}}
   */
  #createCommonBoardTeamMatchGames(ctx) {
    const { tournament, side1Team, side2Team, controlType, byePoints } = ctx;
    const games = [];
    let nextNumber = ctx.startNumber;
    let nextBoard = ctx.startBoard;
    const side1Entries = ctx.getTeamEntries?.(side1Team) ?? [];
    const side2Entries = ctx.getTeamEntries?.(side2Team) ?? [];
    const side1Players = this.#entriesToPlayers(side1Entries);
    const side2Players = this.#entriesToPlayers(side2Entries);

    if (!side1Players.length) {
      throw new Error('Side1 team has no active players for common-board team match generation');
    }

    const commonBoardGame = this.#createTeamBaseGame(ctx, {
      whitePlayerIds: side1Players.map((player) => player?.id).filter(Boolean),
      blackPlayerIds: side2Players.map((player) => player?.id).filter(Boolean),
      whiteSide: this.#sideFromParticipantEntries(side1Entries),
      blackSide: this.#sideFromParticipantEntries(side2Entries),
      number: ++nextNumber,
      board: nextBoard++,
      isBye: side2Players.length === 0,
      rated: tournament.rated,
      teamContext: ctx.buildTeamContext(
        this.#teamWithPlayers(side1Team, side1Players),
        this.#teamWithPlayers(side2Team, side2Players)
      ),
    });

    this.#applyTeamRatings(commonBoardGame, side1Team, side2Team, controlType, side1Players, side2Players);

    if (commonBoardGame.isBye) {
      this.#applyByeResult(commonBoardGame, byePoints);
    }

    games.push(commonBoardGame);
    return this.#buildGeneratedGamesResult(games, nextNumber, nextBoard);
  }

  /**
   * Создаёт подосочные Game внутри командного матча.
   * Расстановку игроков по доскам получает из сервиса team-board pairing.
   * @param {Object} ctx
   * @returns {{games: Game[], nextNumberValue: number, nextBoardValue: number}}
   */
  #createBoardTeamMatchGames(ctx) {
    const { tournament, side1Team, side2Team, controlType, playerStandings, playerHistory, mirrorRepeatPairColors } = ctx;
    const games = [];
    let nextNumber = ctx.startNumber;
    let nextBoard = ctx.startBoard;
    const side1Entries = ctx.getTeamEntries?.(side1Team) ?? [];
    const side2Entries = ctx.getTeamEntries?.(side2Team) ?? [];
    const side1MatchTeam = { ...side1Team, players: this.#entriesToPlayers(side1Entries) };
    const side2MatchTeam = { ...side2Team, players: this.#entriesToPlayers(side2Entries) };

    if (!side1MatchTeam.players.length) {
      throw new Error('Side1 team has no active players for team board match generation');
    }

    const plannedAssignments = ctx.pair?.boardAssignments;
    const assignments = plannedAssignments ? plannedAssignments.map(assignment => ({
      ...assignment,
      whiteTeam: assignment.whiteTeam.key === side1MatchTeam.key ? side1MatchTeam : side2MatchTeam,
      blackTeam: assignment.blackTeam.key === side1MatchTeam.key ? side1MatchTeam : side2MatchTeam,
    })) : buildTeamBoardPairingAssignments({
      side1Team: side1MatchTeam,
      side2Team: side2MatchTeam,
      players: tournament.getActivePlayers(),
      teamSize: tournament.getTeamSize(),
      controlType,
      playerStandings,
      playerHistory,
      pastGames: mirrorRepeatPairColors ? tournament.getRegularGames() : [],
      mirrorRepeatPairColors,
      avoidRepeatByes: ctx.avoidRepeatByes,
    });

    const sortedAssignments = ctx.sortBoardByesLast
      ? [...assignments].sort((a, b) => {
          const aIsBye = !a?.whitePlayer?.id || !a?.blackPlayer?.id;
          const bIsBye = !b?.whitePlayer?.id || !b?.blackPlayer?.id;
          if (aIsBye !== bIsBye) return aIsBye ? 1 : -1;
          return 0;
        })
      : assignments;

    for (const assignment of sortedAssignments) {
      const { whitePlayer, blackPlayer, whiteTeam, blackTeam } = assignment;
      const whiteEntries = whiteTeam === side1MatchTeam
        ? side1Entries
        : side2Entries;
      const blackEntries = blackTeam === side1MatchTeam
        ? side1Entries
        : side2Entries;
      const game = this.#createTeamBaseGame(ctx, {
        whitePlayerIds: whitePlayer?.id ? [whitePlayer.id] : [],
        blackPlayerIds: blackPlayer?.id ? [blackPlayer.id] : [],
        whiteSide: this.#sideFromParticipantEntries([this.#entryForPlayer(whiteEntries, whitePlayer)]),
        blackSide: this.#sideFromParticipantEntries([this.#entryForPlayer(blackEntries, blackPlayer)]),
        number: ++nextNumber,
        board: nextBoard++,
        isBye: !whitePlayer?.id || !blackPlayer?.id,
        rated: tournament.rated,
        teamContext: ctx.buildTeamContext(whiteTeam, blackTeam),
      });

      this.#applySinglePlayerRatings(game, whitePlayer, blackPlayer, controlType);

      if (game.isBye && ctx.teamMatchFormat !== TeamMatchFormat.FIRST_WIN) {
        game.setResult(whitePlayer?.id ? '1-0' : '0-1');
      }

      games.push(game);
    }

    return this.#buildGeneratedGamesResult(games, nextNumber, nextBoard);
  }

  /**
   * Создаёт базовую Game для командного матча, подставляя общие параметры из контекста.
   * @param {Object} ctx
   * @param {Object} params
   * @returns {Game}
   */
  #createTeamBaseGame(ctx, {
    whitePlayerIds,
    blackPlayerIds,
    whiteSide = null,
    blackSide = null,
    number,
    board,
    isBye,
    rated,
    teamContext,
  }) {
    return this.#createBaseGame({
      tournament: ctx.tournament,
      whitePlayerIds,
      blackPlayerIds,
      whiteSide,
      blackSide,
      round: ctx.round,
      cycle: ctx.cycle,
      number,
      board,
      stage: ctx.stage,
      stageNumber: ctx.stageNumber,
      level: ctx.level,
      bracketIndex: ctx.bracketIndex,
      isBye,
      rated,
      controlType: ctx.controlType,
      teamContext,
    });
  }

  /**
   * Единая точка создания Game.fromObject для фабрики.
   * @param {Object} params
   * @returns {Game}
   */
  #createBaseGame({
    tournament,
    whitePlayerIds = [],
    blackPlayerIds = [],
    whiteSide = null,
    blackSide = null,
    round = null,
    cycle = null,
    number = null,
    board = null,
    stage = GameStage.REGULAR,
    stageNumber = null,
    level = null,
    bracketIndex = null,
    isBye = false,
    rated = false,
    controlType = null,
    teamContext = null,
  }) {
    const resolvedWhiteSide = whiteSide ?? this.#buildSideFromPlayers(
      whitePlayerIds.map((playerId) => tournament.players?.find((player) => player?.id === playerId)).filter(Boolean),
      tournament
    );
    const resolvedBlackSide = blackSide ?? this.#buildSideFromPlayers(
      blackPlayerIds.map((playerId) => tournament.players?.find((player) => player?.id === playerId)).filter(Boolean),
      tournament
    );

    return Game.fromObject({
      tournamentId: tournament.id,
      whitePlayerIds: Game.playerIdsFromSide(resolvedWhiteSide),
      blackPlayerIds: Game.playerIdsFromSide(resolvedBlackSide),
      whiteSide: resolvedWhiteSide,
      blackSide: resolvedBlackSide,
      round,
      cycle,
      number,
      board,
      stage,
      stageNumber,
      level,
      bracketIndex,
      isBye,
      rated,
      controlType,
      teamContext,
    });
  }

  /**
   * Проставляет результат bye-партии по настройке очков за bye.
   * @param {Game} game
   * @param {number} byePoints
   * @returns {void}
   */
  #applyByeResult(game, byePoints) {
    if (byePoints === 1) {
      game.setResult('1-0');
    } else if (byePoints === 0.5) {
      game.setResult('½-½');
    } else {
      game.setResult('0-1');
    }
  }

  /**
   * Копирует стартовые рейтинги игроков в Game для обычной досочной партии.
   * @param {Game} game
   * @param {import('@/models/Player').default|null} whitePlayer
   * @param {import('@/models/Player').default|null} blackPlayer
   * @param {string|null} controlType
   * @returns {void}
   */
  #applySinglePlayerRatings(game, whitePlayer, blackPlayer, controlType) {
    const whiteFromMap = whitePlayer?.ratingsMap?.[controlType];
    const blackFromMap = blackPlayer?.ratingsMap?.[controlType];
    game.whiteRating = (whiteFromMap && typeof whiteFromMap.rating === 'number') ? whiteFromMap.rating : null;
    game.blackRating = (blackFromMap && typeof blackFromMap.rating === 'number') ? blackFromMap.rating : null;
  }

  /**
   * Копирует средние рейтинги команд в Game для manual/common-board режимов.
   * @param {Game} game
   * @param {import('@/models/Team').default|null} whiteTeam
   * @param {import('@/models/Team').default|null} blackTeam
   * @param {string|null} controlType
   * @param {Array<import('@/models/Player').default>|null} [whitePlayers=null]
   * @param {Array<import('@/models/Player').default>|null} [blackPlayers=null]
   * @returns {void}
   */
  #applyTeamRatings(game, whiteTeam, blackTeam, controlType, whitePlayers = null, blackPlayers = null) {
    game.whiteRating = Team.getRatingInfoForPlayers(controlType, whitePlayers)?.rating ?? null;
    game.blackRating = blackTeam ? (Team.getRatingInfoForPlayers(controlType, blackPlayers)?.rating ?? null) : null;
  }

  /**
   * Возвращает созданные игры вместе со следующими значениями number/board для вызывающего генератора.
   * @param {Game[]} games
   * @param {number} nextNumber
   * @param {number} nextBoard
   * @returns {{games: Game[], nextNumberValue: number, nextBoardValue: number}}
   */
  #buildGeneratedGamesResult(games, nextNumber, nextBoard) {
    return {
      games,
      nextNumberValue: nextNumber,
      nextBoardValue: nextBoard,
    };
  }

  /**
   * Собирает teamContext для fixed-team Game.
   * В Game сохраняется минимальная идентифицирующая информация о командах и ключ группировки.
   * @param {import('@/models/Team').default|null} whiteTeam
   * @param {import('@/models/Team').default|null} blackTeam
   * @param {string} teamMatchKey
   * @returns {Object}
   */
  #buildFixedTeamContext(whiteTeam, blackTeam, teamMatchKey) {
    return {
      whiteTeam: whiteTeam
        ? {
            id: whiteTeam.id,
            name: whiteTeam.name,
          }
        : null,
      blackTeam: blackTeam
        ? {
            id: blackTeam.id,
            name: blackTeam.name,
          }
        : null,
      whiteTeamKey: null,
      blackTeamKey: null,
      teamMatchKey,
    };
  }
}

export default new GameFactory();
